import axios from 'axios';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Create axios instance with interceptors for authentication
const apiClient = axios.create();

// Promise to track if Firebase Auth is ready
let authInitialized = false;
let authReadyPromise: Promise<void>;

// Initialize auth ready promise
authReadyPromise = new Promise((resolve) => {
  if (authInitialized) {
    resolve();
    return;
  }

  const unsubscribe = onAuthStateChanged(auth, (user) => {
    authInitialized = true;
    unsubscribe(); // Stop listening after first state change
    resolve();
  });

  // Timeout after 10 seconds to prevent hanging
  setTimeout(() => {
    if (!authInitialized) {
      authInitialized = true;
      unsubscribe();
      resolve();
    }
  }, 10000);
});

// Add auth header to all requests
apiClient.interceptors.request.use(
  async (config) => {
    // Wait for Firebase Auth to be ready
    await authReadyPromise;

    const user = auth.currentUser;
    if (user) {
      try {
        // Force refresh token to ensure it's valid
        const token = await user.getIdToken(true);

        // Validate token format before sending
        if (token && token.length > 100 && token.includes('.')) {
          config.headers['Authorization'] = `Bearer ${token}`;
          console.log('Firebase token attached, length:', token.length);
        } else {
          console.warn('Invalid token format detected, length:', token?.length || 0);
          throw new Error('Invalid token format');
        }
      } catch (error) {
        console.error('Error getting Firebase token:', error);
        // Don't attach invalid token
      }
    } else {
      console.log('No authenticated user found');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Redirect to login or show error
      console.error('Authentication error:', error.response.data);
    }
    return Promise.reject(error);
  }
);

export default apiClient;